import { useMemo, useRef, useCallback, useEffect, useState } from 'react';
import debounce from 'lodash.debounce';
import { toast } from 'react-toastify';
import { saveBcmPhase } from '../services/bcmService';

export const useBcmAutoSave = ({
  getSelectedPhase,
  assessmentIds,
  submissionStatus,
  onResponseIdSaved,
  onSaved,
  onSaveStateChange,
}) => {
  const queueRef = useRef(new Map());
  const [isBlockedByAuth, setIsBlockedByAuth] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const lastToastRef = useRef('');

  const toastOnce = useCallback((message, type = 'error') => {
    if (lastToastRef.current === message) return;
    lastToastRef.current = message;
    toast[type](message);
    setTimeout(() => {
      if (lastToastRef.current === message) lastToastRef.current = '';
    }, 2500);
  }, []);

  const emitSaveState = useCallback(
    (patch) => {
      onSaveStateChange?.({
        hasUnsyncedChanges: false,
        blockedReason: null,
        lastError: null,
        ...patch,
      });
    },
    [onSaveStateChange]
  );

  useEffect(() => {
    const onAuthExpired = () => {
      setIsBlockedByAuth(true);
      emitSaveState({
        hasUnsyncedChanges: true,
        blockedReason: 'auth',
        lastError: 'Session expired. Please sign in again to continue saving.',
      });
      toastOnce('Session expired. Changes are not saving until you sign in again.', 'error');
    };

    const onAuthRecovered = () => {
      setIsBlockedByAuth(false);
      emitSaveState({
        hasUnsyncedChanges: true,
        blockedReason: isOffline ? 'offline' : null,
        lastError: isOffline ? 'You are offline.' : null,
      });
      if (!isOffline) toastOnce('Session restored. Auto-save resumed.', 'success');
    };

    const onOfflineEvent = () => {
      setIsOffline(true);
      emitSaveState({
        hasUnsyncedChanges: true,
        blockedReason: 'offline',
        lastError: 'You appear offline. Changes will not sync until connection is restored.',
      });
      toastOnce('You are offline. Auto-save paused.', 'error');
    };

    const onOnlineEvent = () => {
      setIsOffline(false);
      const token = localStorage.getItem('access_token');
      if (token) setIsBlockedByAuth(false);

      emitSaveState({
        hasUnsyncedChanges: true,
        blockedReason: token ? null : isBlockedByAuth ? 'auth' : null,
        lastError: token ? null : isBlockedByAuth ? 'Session expired. Please sign in again.' : null,
      });

      toastOnce('Connection restored. Auto-save can continue.', 'info');
    };

    window.addEventListener('auth:expired', onAuthExpired);
    window.addEventListener('auth:recovered', onAuthRecovered);
    window.addEventListener('api:offline', onOfflineEvent);
    window.addEventListener('offline', onOfflineEvent);
    window.addEventListener('online', onOnlineEvent);

    return () => {
      window.removeEventListener('auth:expired', onAuthExpired);
      window.removeEventListener('auth:recovered', onAuthRecovered);
      window.removeEventListener('api:offline', onOfflineEvent);
      window.removeEventListener('offline', onOfflineEvent);
      window.removeEventListener('online', onOnlineEvent);
    };
  }, [emitSaveState, isBlockedByAuth, isOffline, toastOnce]);

  const normalizeAnswer = (answer) => {
    if (answer == null) return null;
    const v = String(answer).trim().toLowerCase();
    if (v === 'yes') return 'yes';
    if (v === 'no') return 'no';
    return null;
  };

  const saveSingleResponse = useCallback(
    async (respObj) => {
      if (!respObj?.question) return null;
      const normalized = normalizeAnswer(respObj.answer);
      if (!normalized) return null;

      if (isBlockedByAuth) {
        const err = new Error('Session expired. Please sign in again.');
        err.code = 'AUTH_BLOCKED';
        throw err;
      }

      if (isOffline || !navigator.onLine) {
        const err = new Error('You are offline. Auto-save paused.');
        err.code = 'OFFLINE_BLOCKED';
        throw err;
      }

      const phase = getSelectedPhase?.();
      if (!phase?.id) return null;
      if (submissionStatus?.[phase.id] === 'submitted') return null;

      const assessmentId = assessmentIds?.[phase.id];
      if (!assessmentId) {
        const err = new Error('Assessment not found for selected phase.');
        err.code = 'MISSING_ASSESSMENT';
        throw err;
      }

      const saveRes = await saveBcmPhase(phase.id, {
        assessment_id: assessmentId,
        responses: [
          {
            question_id: respObj.question,
            performed: normalized,
            evidence_text: respObj.evidence || '',
          },
        ],
      });

      const savedResp = saveRes?.data?.saved?.[0];
      if (!savedResp?.id) throw new Error('No saved response id returned');

      onResponseIdSaved?.(respObj.question, savedResp.id);
      onSaved?.();

      emitSaveState({ hasUnsyncedChanges: false, blockedReason: null, lastError: null });
      return savedResp.id;
    },
    [getSelectedPhase, submissionStatus, assessmentIds, onResponseIdSaved, onSaved, isBlockedByAuth, isOffline, emitSaveState]
  );

  const enqueueSave = useCallback(
    (respObj) => {
      emitSaveState({ hasUnsyncedChanges: true });
      const key = respObj?.question;
      if (!key) return Promise.resolve(null);

      const previous = queueRef.current.get(key) || Promise.resolve();

      const next = previous
        .catch(() => null)
        .then(async () => {
          try {
            return await saveSingleResponse(respObj);
          } catch (err) {
            const msg =
              err?.code === 'AUTH_BLOCKED'
                ? 'Session expired. Please sign in again.'
                : err?.code === 'OFFLINE_BLOCKED'
                ? 'Offline. Auto-save paused.'
                : err?.response?.status === 401
                ? 'Session expired. Please sign in again.'
                : err?.code === 'MISSING_ASSESSMENT'
                ? 'Assessment is not ready yet. Please retry in a moment.'
                : !err?.response
                ? 'Network error. Auto-save paused until connection returns.'
                : err?.response?.data?.error || 'Auto-save failed';

            const blockedReason =
              err?.code === 'AUTH_BLOCKED' || err?.response?.status === 401
                ? 'auth'
                : err?.code === 'OFFLINE_BLOCKED' || !err?.response
                ? 'offline'
                : null;

            emitSaveState({ hasUnsyncedChanges: true, blockedReason, lastError: msg });
            toastOnce(msg, 'error');
            return null;
          }
        });

      queueRef.current.set(key, next);
      return next;
    },
    [saveSingleResponse, emitSaveState, toastOnce]
  );

  const debouncedSave = useMemo(() => debounce((respObj) => enqueueSave(respObj), 1200), [enqueueSave]);

  useEffect(() => () => debouncedSave.cancel(), [debouncedSave]);

  return { saveSingleResponse, enqueueSave, debouncedSave, isBlockedByAuth, isOffline };
};